import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  DataService,
  FormInputComponent,
  NotificationService,
  SharedModule,
  StringCustomFieldConfig,
} from '@vendure/admin-ui/core';
import { isObservable, Subject, Subscription, takeUntil } from 'rxjs';
import { Collection, Product } from '../generated';
import {
  PolicyContentData,
  NavContext,
  NavigationItem,
  NavigationTargetType,
  Page,
  PageComponentData,
  defaultNavigationItem,
} from '../ui-types';
import {
  safeJSONParse,
  sectionIdsFromPageComponents,
  sectionsForSlug,
} from '../utils';
import { CURRENT_PAGE_SLUG } from '../constants';

const BUILT_IN_BASE_PAGES: Array<{ value: string; label: string }> = [
  { value: 'orderstatus', label: 'Order Status' },
  { value: 'contact-us', label: 'Contact Us' },
  { value: 'terms-of-service', label: 'Terms of Service' },
  { value: 'privacy-policy', label: 'Privacy Policy' },
];

@Component({
  selector: 'vdr-navigation-item-input',
  template: `
    <vdr-card class="mb-2">
      <div class="form-row">
        <vdr-form-field *ngIf="showLabel" [label]="'Label'" class="form-item">
          <input type="text" [formControl]="labelControl" />
        </vdr-form-field>

        <vdr-form-field [label]="'Target Type'" class="form-item">
          <select [formControl]="targetTypeControl">
            <option *ngFor="let type of navigationTargetTypes" [value]="type.value">
              {{ type.label }}
            </option>
          </select>
        </vdr-form-field>

        <vdr-form-field [label]="'Target'" class="form-item">
          <ng-container [ngSwitch]="targetTypeControl.value">
            <select
              *ngSwitchCase="NavigationTargetType.SitePage"
              [formControl]="targetValueControl"
              (change)="onTargetPageSelectionChange($any($event.target).value)"
            >
              <option value="" disabled>Select a page</option>
              <option *ngIf="missingSitePage" [value]="missingSitePage">
                Missing page: {{ missingSitePage }}
              </option>
              <option *ngFor="let page of filteredPages" [value]="page.slug">
                {{ page.name }} &lt;{{ page.slug }}&gt;
              </option>
            </select>

            <select
              *ngSwitchCase="NavigationTargetType.SitePageSection"
              [formControl]="targetValueControl"
              (change)="onTargetPageSelectionChange($any($event.target).value)"
            >
              <option value="" disabled>Select a page</option>
              <option *ngIf="enableCurrentPage" [value]="CURRENT_PAGE_SLUG">Current Page</option>
              <option *ngIf="missingSitePage" [value]="missingSitePage">
                Missing page: {{ missingSitePage }}
              </option>
              <option *ngFor="let page of filteredPages" [value]="page.slug">
                {{ page.name }} &lt;{{ page.slug }}&gt;
              </option>
            </select>

            <select
              *ngSwitchCase="NavigationTargetType.BuiltInSitePage"
              [formControl]="targetValueControl"
            >
              <option value="" disabled>Select a built-in page</option>
              <option
                *ngIf="missingBuiltInPage"
                [value]="missingBuiltInPage"
              >
                Missing page: {{ missingBuiltInPage }}
              </option>
              <option *ngFor="let page of builtInPages" [value]="page.value">
                {{ page.label }} &lt;{{ page.value }}&gt;
              </option>
            </select>
            <input
              *ngSwitchCase="NavigationTargetType.Website"
              type="text"
              [formControl]="targetValueControl"
              placeholder="https://example.com/path"
            />
            <input
              *ngSwitchCase="NavigationTargetType.Custom"
              type="text"
              [formControl]="targetValueControl"
              placeholder="/custom/path"
            />
          </ng-container>
        </vdr-form-field>
      </div>

      <div class="form-row" *ngIf="showSectionRow">
        <vdr-form-field
          *ngIf="targetTypeControl.value === NavigationTargetType.SitePageSection"
          [label]="'Page Section'"
          class="form-item"
        >
          <select [formControl]="pageSectionControl" [disabled]="!availableSections.length">
            <option value="" disabled>
              {{ availableSections.length ? 'Select a section' : 'No sections available' }}
            </option>
            <option *ngFor="let section of availableSections" [value]="section">
              {{ section }}
            </option>
          </select>
        </vdr-form-field>
      </div>

      <div class="form-row" *ngIf="showProductCollectionRow">
        <vdr-form-field *ngIf="showProductSelector" [label]="'Target Product'" class="form-item">
          <vdr-dynamic-form-input
            [control]="targetProductControl"
            [def]="{
              type: 'relation',
              entity: 'Product',
              ui: { component: 'relation-form-input' }
            }"
          ></vdr-dynamic-form-input>
        </vdr-form-field>

        <vdr-form-field *ngIf="showCollectionSelector" [label]="'Target Collection'" class="form-item">
          <vdr-dynamic-form-input
            [control]="targetCollectionControl"
            [def]="{
              type: 'relation',
              entity: 'Collection',
              ui: { component: 'relation-collection-selector' }
            }"
          ></vdr-dynamic-form-input>
        </vdr-form-field>
      </div>
    </vdr-card>
  `,
  styleUrls: ['../styles.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class NavigationItemInputComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
  readonly: boolean;
  formControl: FormControl;

  labelControl = new FormControl('');
  targetTypeControl = new FormControl();
  targetValueControl = new FormControl();
  pageSectionControl = new FormControl();
  targetProductControl = new FormControl();
  targetCollectionControl = new FormControl();

  navigationItemData!: NavigationItem;
  navigationTargetTypes = [
    { value: NavigationTargetType.SitePage, label: 'Page' },
    { value: NavigationTargetType.SitePageSection, label: 'Page Section' },
    { value: NavigationTargetType.BuiltInSitePage, label: 'Built-in Page' },
    { value: NavigationTargetType.Website, label: 'Website' },
    { value: NavigationTargetType.Custom, label: 'Custom' },
  ];

  navCtx?: NavContext;
  enableCurrentPage = false;
  showLabel = true;
  filteredPages: Page[] = [];
  availableSections: string[] = [];
  policyContentList: PolicyContentData[] = [];
  builtInPages: Array<{ value: string; label: string }> = [];
  NavigationTargetType = NavigationTargetType;

  readonly CURRENT_PAGE_SLUG = CURRENT_PAGE_SLUG;

  private destroy$ = new Subject<void>();
  private policySub?: Subscription;
  private pagesSub?: Subscription;
  private pageComponentsSub?: Subscription;
  private currentPageComponents: PageComponentData[] = [];

  private _config!: StringCustomFieldConfig;
  get config(): StringCustomFieldConfig {
    return this._config;
  }
  set config(value: StringCustomFieldConfig) {
    this._config = value;
    const options: any = value?.ui?.options || {};
    this.navCtx = options.navContext as NavContext | undefined;
    this.enableCurrentPage = this.navCtx?.enableCurrentPage ?? options.enableCurrentPage ?? false;
    this.showLabel = options.hideLabel !== true;

    this.pagesSub?.unsubscribe();
    const applyPages = (pages: Page[]) => {
      this.filteredPages = pages ?? [];
      if (this.navigationItemData?.targetType === NavigationTargetType.SitePageSection) {
        this.refreshSections(this.targetValueControl.value);
      }
      this.changeDetector.markForCheck();
    };

    if (this.navCtx?.filteredPages$) {
      this.pagesSub = this.navCtx.filteredPages$
        .pipe(takeUntil(this.destroy$))
        .subscribe((pages: any) => applyPages(pages ?? []));
    } else if (options.pages$ && isObservable(options.pages$)) {
      this.pagesSub = options.pages$
        .pipe(takeUntil(this.destroy$))
        .subscribe((pages: any) => applyPages(pages ?? []));
    } else if (Array.isArray(options.pages)) {
      applyPages(options.pages as Page[]);
    }

    this.pageComponentsSub?.unsubscribe();
    const applyCurrentComponents = (components: PageComponentData[]) => {
      this.currentPageComponents = components ?? [];
      if (
        this.targetTypeControl.value === NavigationTargetType.SitePageSection &&
        this.targetValueControl.value === CURRENT_PAGE_SLUG
      ) {
        this.refreshSections(CURRENT_PAGE_SLUG);
      }
      this.changeDetector.markForCheck();
    };
    if (options.pageComponentChanges && isObservable(options.pageComponentChanges)) {
      this.pageComponentsSub = options.pageComponentChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((components: any) => applyCurrentComponents(components ?? []));
    } else {
      applyCurrentComponents((options.currentPageComponents ?? []) as PageComponentData[]);
    }

    this.policySub?.unsubscribe();
    const policy$ = this.navCtx?.policyContentList$ ?? options.policyContentList$;
    const applyPolicies = (list: PolicyContentData[]) => {
      this.policyContentList = list ?? [];
      this.updateBuiltInPages();
      this.changeDetector.markForCheck();
    };

    if (policy$ && isObservable(policy$)) {
      this.policySub = policy$
        .pipe(takeUntil(this.destroy$))
        .subscribe((list: any) => applyPolicies(list ?? []));
    } else {
      const fallbackList = options.policyContentList ?? [];
      applyPolicies(fallbackList as PolicyContentData[]);
    }
  }

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.navigationItemData = this.parseNavigationItem(this.formControl.value, { ...defaultNavigationItem });
    this.initializeFormControls();

    if (this.navigationItemData.targetProductId) {
      this.dataService.product
        .getProduct(this.navigationItemData.targetProductId.toString())
        .mapSingle(data => data.product)
        .pipe(takeUntil(this.destroy$))
        .subscribe(product => this.targetProductControl.setValue(product));
    }

    if (this.navigationItemData.targetCollectionId) {
      this.dataService.collection.getCollections({
        filter: { id: { eq: this.navigationItemData.targetCollectionId.toString() } },
      })
        .mapSingle(data => data.collections?.items[0])
        .pipe(takeUntil(this.destroy$))
        .subscribe(collection => this.targetCollectionControl.setValue(collection));
    }

  }

  ngAfterViewInit(): void {
    this.labelControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onLabelChange);
    this.targetTypeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTargetTypeChange);
    this.targetValueControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTargetValueChange);
    this.pageSectionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onPageSectionChange);
    this.targetProductControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTargetProductChange);
    this.targetCollectionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(this.onTargetCollectionChange);
  }

  ngOnDestroy(): void {
    this.policySub?.unsubscribe();
    this.pagesSub?.unsubscribe();
    this.pageComponentsSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showProductSelector(): boolean {
    return this.isProductPage(this.selectedPageSlug);
  }

  get showCollectionSelector(): boolean {
    return this.isCollectionPage(this.selectedPageSlug);
  }

  get showSectionRow(): boolean {
    return this.targetTypeControl.value === NavigationTargetType.SitePageSection || this.navigationItemData.pageSection !== '';
  }

  get showProductCollectionRow(): boolean {
    return this.showProductSelector || this.showCollectionSelector;
  }

  get missingBuiltInPage(): string | null {
    if (this.targetTypeControl.value !== NavigationTargetType.BuiltInSitePage) return null;
    const selected = (this.navigationItemData?.builtInPage || '').trim();
    if (!selected) return null;
    const exists = this.builtInPages.some(page => page.value === selected);
    return exists ? null : selected;
  }

  get missingSitePage(): string | null {
    if (
      this.targetTypeControl.value !== NavigationTargetType.SitePage &&
      this.targetTypeControl.value !== NavigationTargetType.SitePageSection
    ) {
      return null;
    }
    const selected = (this.targetValueControl.value || '').toString().trim();
    if (!selected || selected === CURRENT_PAGE_SLUG) return null;
    const exists = this.filteredPages.some(page => (page.slug || '').trim() === selected);
    return exists ? null : selected;
  }

  private get selectedPageSlug(): string {
    if (
      this.targetTypeControl.value === NavigationTargetType.SitePage ||
      this.targetTypeControl.value === NavigationTargetType.SitePageSection
    ) {
      return (this.targetValueControl.value || '').toString();
    }
    return '';
  }

  private initializeFormControls(): void {
    this.labelControl.setValue(this.navigationItemData.label ?? '', { emitEvent: false });
    this.targetTypeControl.setValue(this.navigationItemData.targetType ?? NavigationTargetType.SitePage, { emitEvent: false });
    this.targetValueControl.setValue(this.getTargetValueForType(this.navigationItemData), { emitEvent: false });
    this.pageSectionControl.setValue(this.navigationItemData.pageSection ?? '', { emitEvent: false });
    this.refreshSections(this.navigationItemData.pageSlug ?? '');
  }

  private parseNavigationItem(raw: string | null | undefined, fallback: NavigationItem): NavigationItem {
    const parsed = safeJSONParse<NavigationItem>(raw, this.notificationService) ?? fallback;
    return parsed; //normalizeNavigationItem(parsed);
  }

  private updateBuiltInPages(): void {
    const policyPages = (this.policyContentList ?? []).map(policy => ({
      value: policy.type,
      label: policy.title,
    }));
    const seen = new Set<string>();
    const merged = [...policyPages, ...BUILT_IN_BASE_PAGES].filter(page => {
      if (!page.value || seen.has(page.value)) return false;
      seen.add(page.value);
      return true;
    });
    this.builtInPages = merged;
  }

  onTargetPageSelectionChange(slug: string): void {
    this.refreshSections(slug);
    if (this.navigationItemData?.pageSlug !== slug) {
      this.pageSectionControl.setValue('', { emitEvent: true });
    }
  }

  private refreshSections(slug: string | null | undefined): void {
    if (this.targetTypeControl.value !== NavigationTargetType.SitePageSection) {
      this.availableSections = [];
      return;
    }
    const normalizedSlug = (slug || '').trim();
    if (this.navCtx) {
      this.availableSections = sectionsForSlug(this.navCtx, normalizedSlug, this.notificationService).map(s => s.trim());
    } else if (normalizedSlug === CURRENT_PAGE_SLUG) {
      this.availableSections = sectionIdsFromPageComponents(this.currentPageComponents, this.notificationService).map(s => s.trim());
    } else {
      const page = this.filteredPages.find(p => (p.slug || '').trim() === normalizedSlug);
      this.availableSections = sectionIdsFromPageComponents(page?.pageComponents ?? [], this.notificationService).map(s => s.trim());
    }
    this.changeDetector.markForCheck();
  }

  private onLabelChange = (value: string | null) => {
    const v = value ?? '';
    if (this.navigationItemData.label !== v) {
      this.navigationItemData.label = v;
      this.updateJsonString();
    }
  };

  private onTargetTypeChange = (value: NavigationTargetType | string | null) => {
    const v = (value as NavigationTargetType) ?? NavigationTargetType.SitePage;
    if (this.navigationItemData.targetType !== v) {
      this.navigationItemData.targetType = v;
    }

    if (v !== NavigationTargetType.SitePage && v !== NavigationTargetType.SitePageSection) {
      this.navigationItemData.pageSlug = '';
      this.navigationItemData.pageSection = '';
      this.navigationItemData.targetProductId = null;
      this.navigationItemData.targetCollectionId = null;
      this.targetProductControl.setValue(null, { emitEvent: false });
      this.targetCollectionControl.setValue(null, { emitEvent: false });
    }

    if (v !== NavigationTargetType.SitePageSection) {
      this.navigationItemData.pageSection = '';
      this.pageSectionControl.setValue('', { emitEvent: false });
    }

    if (v !== NavigationTargetType.BuiltInSitePage) {
      this.navigationItemData.builtInPage = '';
    }

    if (v !== NavigationTargetType.Website) {
      this.navigationItemData.websiteUrl = '';
    }

    if (v !== NavigationTargetType.Custom) {
      this.navigationItemData.customSlug = '';
    }

    this.targetValueControl.setValue(this.getTargetValueForType(this.navigationItemData), { emitEvent: false });
    this.refreshSections(this.navigationItemData.pageSlug ?? '');
    this.updateJsonString();
  };

  private onTargetValueChange = (value: string | null) => {
    const v = (value ?? '').trim();
    switch (this.targetTypeControl.value) {
      case NavigationTargetType.SitePage:
      case NavigationTargetType.SitePageSection:
        if (this.navigationItemData.pageSlug !== v) {
          this.navigationItemData.pageSlug = v;
          this.reconcileProductCollectionForPage(v);
          if (this.targetTypeControl.value === NavigationTargetType.SitePageSection) {
            this.refreshSections(v);
            const currentSel = (this.pageSectionControl.value || '').trim();
            if (currentSel && !this.availableSections.includes(currentSel)) {
              this.pageSectionControl.setValue('', { emitEvent: true });
            }
          }
          this.updateJsonString();
        }
        break;
      case NavigationTargetType.BuiltInSitePage:
        if (this.navigationItemData.builtInPage !== v) {
          this.navigationItemData.builtInPage = v;
          this.updateJsonString();
        }
        break;
      case NavigationTargetType.Website:
        if (this.navigationItemData.websiteUrl !== v) {
          this.navigationItemData.websiteUrl = v;
          this.updateJsonString();
        }
        break;
      case NavigationTargetType.Custom:
        if (this.navigationItemData.customSlug !== v) {
          this.navigationItemData.customSlug = v;
          this.updateJsonString();
        }
        break;
      default:
        break;
    }
  };

  private onPageSectionChange = (value: string | null) => {
    const v = (value ?? '').trim();
    if (this.navigationItemData.pageSection !== v) {
      this.navigationItemData.pageSection = v;
      this.updateJsonString();
    }
  };

  private onTargetProductChange = (value: Product | null) => {
    const targetProductId = value ? value.id : null;
    if (this.navigationItemData.targetProductId !== targetProductId) {
      this.navigationItemData.targetProductId = targetProductId;
      this.navigationItemData.targetProductSlug = value?.slug ?? undefined;
      this.updateJsonString();
    }
  };

  private onTargetCollectionChange = (value: Collection | null) => {
    const targetCollectionId = value ? value.id : null;
    if (this.navigationItemData.targetCollectionId !== targetCollectionId) {
      this.navigationItemData.targetCollectionId = targetCollectionId;
      this.navigationItemData.targetCollectionSlug = value?.slug ?? undefined;
      this.updateJsonString();
    }
  };

  private updateJsonString(): void {
    //const canonical = navigationItemToCanonicalNavigationAction(this.navigationItemData);
    const jsonString = JSON.stringify(this.navigationItemData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }

  private getTargetValueForType(item: NavigationItem): string {
    switch (item.targetType) {
      case NavigationTargetType.SitePage:
      case NavigationTargetType.SitePageSection:
        return item.pageSlug ?? '';
      case NavigationTargetType.BuiltInSitePage:
        return item.builtInPage ?? '';
      case NavigationTargetType.Website:
        return item.websiteUrl ?? '';
      case NavigationTargetType.Custom:
        return item.customSlug ?? '';
      default:
        return '';
    }
  }

  private normalizeSlug(slug: string | null | undefined): string {
    return (slug || '').trim().toLowerCase().replace(/\/+$/, '');
  }

  private isProductPage(slug: string | null | undefined): boolean {
    const normalizedSlug = this.normalizeSlug(slug);
    return normalizedSlug === 'dp' || normalizedSlug === '/dp';
  }

  private isCollectionPage(slug: string | null | undefined): boolean {
    const normalizedSlug = this.normalizeSlug(slug);
    return normalizedSlug === 'gp' || normalizedSlug === '/gp';
  }

  private reconcileProductCollectionForPage(slug: string): void {
    if (this.isProductPage(slug)) {
      if (this.navigationItemData.targetCollectionId) {
        this.navigationItemData.targetCollectionId = null;
        this.targetCollectionControl.setValue(null, { emitEvent: false });
      }
      return;
    }

    if (this.isCollectionPage(slug)) {
      if (this.navigationItemData.targetProductId) {
        this.navigationItemData.targetProductId = null;
        this.targetProductControl.setValue(null, { emitEvent: false });
      }
      return;
    }

    if (this.navigationItemData.targetProductId || this.navigationItemData.targetCollectionId) {
      this.navigationItemData.targetProductId = null;
      this.navigationItemData.targetCollectionId = null;
      this.targetProductControl.setValue(null, { emitEvent: false });
      this.targetCollectionControl.setValue(null, { emitEvent: false });
    }
  }
}
